import { useEffect } from "react";
import axios from "axios";
import { toast } from "../../components/Toast/toast.ts";
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import AuthLayout from "../../components/ui/AuthLayout.tsx";
import AppButton from "../../components/ui/AppButton.tsx";
import PasswordInput from "../../components/ui/PasswordInput.tsx";
import {
  resetPasswordSchema,
  ResetPasswordFormData,
} from "./resetPassword.schema.ts";
import PasswordStrengthBar from "../../components/PasswordField/PasswordStrengthBar.tsx";

export default function ResetPassword() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");

  const {
    register,
    handleSubmit,
    watch,
    formState: {
      errors,
      isSubmitting,
      isValid,
    },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onChange",
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const password = watch("newPassword");

  /* =====================================================
      TOKEN VALIDATION
  ====================================================== */

  useEffect(() => {
    if (!token) {
      toast.error("Invalid or expired reset link");

      navigate("/login");
    }
  }, [token, navigate]);

  /* =====================================================
      SUBMIT
  ====================================================== */

  const onSubmit = async (
    data: ResetPasswordFormData
  ) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/cust-portal/reset-pwd`,
        {
          newPassword: data.newPassword,
          confirmPassword: data.confirmPassword,
          token,
        }
      );

      toast.success(
        "Password updated successfully"
      );

      navigate("/login");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          "Failed to reset password"
      );
    }
  };

  return (
    <AuthLayout>
      <div className="flex flex-col gap-7">

        {/* =====================================================
            HEADER
        ====================================================== */}

        <div>
          <span className="page-breadcrumb">
            RESET PASSWORD
          </span>

          <h1
            className="
              mt-3
              page-title
            "
          >
            Create new password
          </h1>

          <p
            className="
              mt-3
              page-subtitle
              leading-7
            "
          >
            Your new password must be different
            from previously used passwords.
          </p>
        </div>

        {/* =====================================================
            FORM
        ====================================================== */}

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-5"
        >
          {/* NEW PASSWORD */}

          <div className="flex flex-col gap-2">
            <PasswordInput
              label="New Password"
              placeholder="Enter new password"
              autoComplete="new-password"
              {...register("newPassword")}
              error={errors.newPassword?.message}
            />

            <PasswordStrengthBar
              password={password || ""}
            />
          </div>

          {/* CONFIRM PASSWORD */}

          <PasswordInput
            label="Confirm Password"
            placeholder="Confirm new password"
            autoComplete="new-password"
            {...register("confirmPassword")}
            error={
              errors.confirmPassword?.message
            }
          />

          {/* ACTIONS */}

          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="
                text-[13px]
                font-medium
                text-[rgb(var(--text-muted))]
                transition-all
                duration-200

                hover:text-[rgb(var(--color-gold-2))]
              "
            >
              Back to Sign In
            </button>
          </div>

          {/* SUBMIT */}

          <AppButton
            type="submit"
            fullWidth
            loading={isSubmitting}
            disabled={!isValid || isSubmitting}
          >
            Reset Password
          </AppButton>
        </form>
      </div>
    </AuthLayout>
  );
}